import ReactDOM from 'react-dom';
import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import html3pdf from 'html3pdf';
import { QRCodeCanvas } from 'qrcode.react';
import useThemeStore from "../../store/themeStore";
import { ThemeProps } from "../../utils/theme";
import { lighten } from 'polished';
import useIsMobile from '../../hooks/useIsMobile';

// Utility functions
const formatNumber = (value: number): string => {
  return value ? value.toLocaleString() : 'N/A';
};

const isWhiteOrShade = (color: string): boolean => {
  if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
  if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
  const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
  if (rgba) {
    const [, r, g, b] = rgba.map(Number);
    return r >= 240 && g >= 240 && b >= 240;
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getBackgroundColor = (theme: any): string => {
  if (isWhiteOrShade(theme.color1)) {
    return lighten(0.6, theme.color2);
  } else {
    return lighten(0.6, theme.color1);
  }
};

// Financial calculation function
const calculateFinancials = (property: any) => {
  const price = property.results.price;
  const loanPercentage = 0.75; // 75% LTV
  const interestRate = 5.0; // 5% interest rate
  
  // Calculate deposit
  const deposit = Math.ceil(price * (1 - loanPercentage));
  
  // Calculate brokerage (1% of borrowing)
  const brokage = Math.ceil(price * loanPercentage * 0.01);
  
  // Calculate stamp duty
  let stampDuty = 0;
  if (price <= 125000) {
    stampDuty = price * 0.05;  // 5% surcharge on additional properties
  } else if (price <= 250000) {
    stampDuty = 125000 * 0.05 + (price - 125000) * 0.07;  // 5% + 2% = 7%
  } else if (price <= 925000) {
    stampDuty = 125000 * 0.05 + 125000 * 0.07 + (price - 250000) * 0.10;  // 5% + 5% = 10%
  } else if (price <= 1500000) {
    stampDuty = 125000 * 0.05 + 125000 * 0.07 + 675000 * 0.10 + (price - 925000) * 0.15;  // 5% + 10% = 15%
  } else {
    stampDuty = 125000 * 0.05 + 125000 * 0.07 + 675000 * 0.10 + 575000 * 0.15 + (price - 1500000) * 0.17;  // 5% + 12% = 17%
  }
  stampDuty = Math.ceil(stampDuty);
  
  // Calculate total investment
  const totalInvestment = Math.ceil(deposit + brokage + stampDuty +
    (property.results.legal || 0) + (property.results.setupFeeValuation || 0));
  
  // Calculate mortgage
  const mortgageAmount = price * loanPercentage;
  const monthlyMortgage = Math.ceil((mortgageAmount * (interestRate / 100)) / 12);
  
  // Calculate net cashflow
  const averageRent = property.results.averageRent || 0;
  const netCashflow = Math.ceil(averageRent - monthlyMortgage);
  const netAnnualCashflow = Math.ceil(netCashflow * 12);
  
  return {
    deposit,
    brokage,
    stampDuty,
    totalInvestment,
    mortgage: monthlyMortgage,
    netCashflow,
    netAnnualCashflow,
    averageRent
  };
};

// PropertyPDFGenerator component
const PropertyPDFGenerator = ({ property, theme }: { property: any, theme: any }) => {
  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );
  
  if (!property?.results) {
    console.error('No property data available');
    return <div>No property data available</div>;
  }

  const availableImages = (property.results.images || []).filter((img: any) => img && img.url);
  const imageSlots = Array(6).fill(null).map((_, index) => availableImages[index] || null);
  const propertyId = property.results._id || property.results.id;
  const websiteUrl = theme.website || window.location.origin;
  const propertyUrl = `${websiteUrl}/listing/${propertyId}`;
  const hideQRCode = !getThemes?.hideQR;

  const financials = calculateFinancials(property);

  const infoItems = [
    { label: "Type", value: property?.results.houseSubtype || 'N/A' },
    { label: "Bedrooms", value: property?.results.bedrooms ?? 'N/A' },
    { label: "Tenure", value: property?.results.tenureType || 'N/A' },
    { label: "Strategy", value: property?.results.strategy || 'N/A' },
    { label: "Price", value: property?.results.price ? `£${formatNumber(property?.results.price)}` : 'N/A' },
    { label: "Zoopla Valuation", value: property?.results.estimatedValue ? `£${formatNumber(property?.results.estimatedValue)}` : 'N/A' },
    { label: "Occupancy", value: property?.results.occupancy || 'N/A' },
    { label: "Gross Yield", value: property?.results.rentGrossYield ? `${property?.results.rentGrossYield}%` : 'N/A' },
    { label: "Current Rent", value: property?.results.currentRent ? `£${formatNumber(property?.results.currentRent)}` : 'N/A' },
    { label: "Estimated Rent", value: property?.results.averageRent ? `£${formatNumber(property?.results.averageRent)}` : 'N/A' },
    { label: "Size", value: property?.results.floorsqft ? `${property?.results.floorsqft} sq ft` : 'N/A' },
    { label: "EPC", value: property?.results.epc || 'N/A' },
    { label: "Flood Risk", value: property?.results.floodRisk || 'N/A' },
  ];

  const comparableTypes = [
    { title: "Previously Sold - Within The Area", data: property?.results.marketComparable },
    { title: "Previously Sold - Same Street", data: property?.results.streetComparable },
  ];

  // Enhanced styles for modern PDF design
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      padding: '0',
      margin: '0',
      fontSize: '12px',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#1f2937',
      backgroundColor: '#ffffff',
    },
    
    // Modern header with solid color and enhanced layout
    header: {
      background: theme.color1,
      padding: '32px',
      borderRadius: '16px 16px 0 0',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      position: 'relative',
      overflow: 'hidden',
    },
    
    headerOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(8px)',
    },
    
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative',
      zIndex: 2,
    },
    
    logoSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    
    logo: {
      maxWidth: '180px',
      maxHeight: '80px',
      objectFit: 'contain',
      filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))',
    },
    
    reportTitle: {
      color: isWhiteOrShade(theme.color1) ? '#1f2937' : '#ffffff',
      fontSize: '24px',
      fontWeight: '700',
      marginTop: '16px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      letterSpacing: '-0.025em',
    },
    
    qrSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    
    qrCode: {
      padding: '8px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    },
    
    qrText: {
      color: isWhiteOrShade(theme.color1) ? '#1f2937' : '#ffffff',
      fontSize: '11px',
      marginTop: '8px',
      fontWeight: '500',
      textAlign: 'center',
    },
    
    // Property header section
    propertyHeader: {
      background: theme.color1,
      padding: '32px',
      borderRadius: '16px',
      margin: '24px 32px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
    },
    
    address: {
      fontSize: '28px',
      fontWeight: '800',
      color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff',
      textAlign: 'center',
      marginBottom: '16px',
      lineHeight: '1.2',
      letterSpacing: '-0.025em',
    },
    
    price: {
      fontSize: '24px',
      fontWeight: '700',
      color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff',
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      marginTop: '16px',
      marginBottom: '16px',
      lineHeight: '1.2',
      whiteSpace: 'pre', // This preserves whitespace
      letterSpacing: '0.025em', // Adds slight letter spacing for better readability
    },
    
    // Image gallery with modern grid
    imageGallery: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      margin: '32px',
      padding: '24px',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
    },
    
    imageContainer: {
      height: '200px',
      borderRadius: '12px',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    },
    
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    
    placeholderImage: {
      width: '100%',
      height: '100%',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '12px',
      border: '2px dashed #cbd5e1',
    },
    
    // Two-column layout for financial and description
    twoColumnSection: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '32px',
      margin: '32px',
    },
    
    // Modern card design for sections
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
      position: 'relative',
      overflow: 'hidden',
    },
    
    cardHeader: {
      borderBottom: '2px solid #e2e8f0',
      paddingBottom: '16px',
      marginBottom: '24px',
      position: 'relative',
    },
    
    cardTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#0f172a',
      marginBottom: '4px',
      letterSpacing: '-0.025em',
    },
    
    cardSubtitle: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500',
    },
    
    // Financial section styling
    financialGrid: {
      display: 'grid',
      gap: '16px',
    },
    
    financialItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    
    financialLabel: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#374151',
    },
    
    financialValue: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#000000',
    },
    
    divider: {
      height: '1px',
      backgroundColor: '#d1d5db',
      margin: '16px 0',
    },
    
    // Property description styling
    descriptionText: {
      fontSize: '13px',
      lineHeight: '1.7',
      color: '#4b5563',
      textAlign: 'justify',
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
    },
    
    // Property information grid
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      margin: '32px',
      padding: '32px',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
    },
    
    infoItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    
    infoLabel: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#374151',
    },
    
    infoValue: {
      fontSize: '13px',
      fontWeight: '500',
      color: '#1f2937',
    },
    
    // Comparables section
    comparablesGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px',
      margin: '32px',
    },
    
    comparableCard: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e2e8f0',
      minHeight: '300px',
    },
    
    comparableTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#0f172a',
      textAlign: 'center',
      marginBottom: '20px',
      paddingBottom: '12px',
      borderBottom: '2px solid #e2e8f0',
    },
    
    comparableItem: {
      padding: '12px',
      marginBottom: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      textAlign: 'center',
    },
    
    comparableAddress: {
      fontSize: '12px',
      color: '#4b5563',
      marginBottom: '4px',
    },
    
    comparablePrice: {
      fontSize: '13px',
      fontWeight: '700',
      color: '#000000',
    },
    
    noData: {
      fontSize: '12px',
      color: '#9ca3af',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: '32px',
    },
    
    // Disclaimer styling
    disclaimer: {
      fontSize: '10px',
      color: '#6b7280',
      lineHeight: '1.6',
      textAlign: 'center',
      padding: '24px 32px',
      backgroundColor: '#f9fafb',
      margin: '32px',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
    },
    
    disclaimerTitle: {
      fontWeight: '700',
      color: '#374151',
      marginBottom: '12px',
      fontSize: '12px',
    },
  };

  const qrCodeStyle = isWhiteOrShade(theme.color1)
    ? {
        backgroundColor: '#000000',
        padding: '8px',
        borderRadius: '8px',
      }
    : {
        backgroundColor: '#ffffff',
        padding: '8px',
        borderRadius: '8px',
      };

  const qrCodeFgColor = isWhiteOrShade(theme.color1) ? '#ffffff' : '#000000';
  const qrCodeBgColor = isWhiteOrShade(theme.color1) ? '#000000' : '#ffffff';

  return (
    <div style={styles.page}>
      {/* Modern Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoSection}>
            <img src={theme.logo} alt="Logo" style={styles.logo} />
            <div style={styles.reportTitle}>Property Investment Report</div>
          </div>
          {!hideQRCode && (
            <div style={styles.qrSection}>
              <div style={{ ...styles.qrCode, ...qrCodeStyle }}>
                <QRCodeCanvas 
                  value={propertyUrl} 
                  size={80} 
                  fgColor={qrCodeFgColor}
                  bgColor={qrCodeBgColor}
                />
              </div>
              <p style={styles.qrText}>Scan to view online</p>
            </div>
          )}
        </div>
      </div>

      {/* Property Header */}
      <div style={styles.propertyHeader}>
        <h1 style={styles.address}>{property.results.propertyOf || 'Property Address'}</h1>
        <p style={styles.price}>
          {'Asking Price: '}£{property.results.price ? formatNumber(property.results.price) : 'N/A'}
        </p>
      </div>

      {/* Image Gallery */}
      <div style={styles.imageGallery}>
        {imageSlots.map((img, index) => (
          <div key={index} style={styles.imageContainer}>
            {img ? (
              <img 
                src={img.url} 
                alt={`Property ${index + 1}`} 
                style={styles.image} 
              />
            ) : (
              <div style={styles.placeholderImage}>
                <span style={{ color: '#94a3b8', fontSize: '12px' }}>No Image Available</span>
              </div>
            )}
          </div>
        ))}
      </div>


      {/* Page break for second page */}
      <div style={{ pageBreakBefore: 'always' }}>
        {/* Header repeat for page 2 */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.logoSection}>
              <img src={theme.logo} alt="Logo" style={styles.logo} />
              <div style={styles.reportTitle}>Property Investment Report</div>
            </div>
            {!hideQRCode && (
              <div style={styles.qrSection}>
                <div style={{ ...styles.qrCode, ...qrCodeStyle }}>
                  <QRCodeCanvas 
                    value={propertyUrl} 
                    size={80} 
                    fgColor={qrCodeFgColor}
                    bgColor={qrCodeBgColor}
                  />
                </div>
                <p style={styles.qrText}>Scan to view online</p>
              </div>
            )}
          </div>
        </div>

        {/* Property Information Grid */}
        <div style={styles.card} className="mt-6">
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Property Specifications</h3>
            <p style={styles.cardSubtitle}>Key details and features</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {infoItems.map((item, index) => (
              <div key={index} style={styles.infoItem}>
                <span style={styles.infoLabel}>{item.label}</span>
                <span style={styles.infoValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Page break for Financial and Description */}
        <div style={{ pageBreakBefore: 'always' }}>
          {/* Header repeat for page 3 */}
          <div style={styles.header}>
            <div style={styles.headerContent}>
              <div style={styles.logoSection}>
                <img src={theme.logo} alt="Logo" style={styles.logo} />
                <div style={styles.reportTitle}>Property Investment Report</div>
              </div>
              {!hideQRCode && (
                <div style={styles.qrSection}>
                  <div style={{ ...styles.qrCode, ...qrCodeStyle }}>
                    <QRCodeCanvas 
                      value={propertyUrl} 
                      size={80} 
                      fgColor={qrCodeFgColor}
                      bgColor={qrCodeBgColor}
                    />
                  </div>
                  <p style={styles.qrText}>Scan to view online</p>
                </div>
              )}
            </div>
          </div>

          {/* Financial Information and Property Description */}
          <div style={styles.twoColumnSection}>
            {/* Financial Information Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Financial Analysis</h3>
                <p style={styles.cardSubtitle}>Investment breakdown and projections</p>
              </div>
              <div style={styles.financialGrid}>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Purchase Price</span>
                  <span style={styles.financialValue}>£{formatNumber(property.results.price)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Deposit (25%)</span>
                  <span style={styles.financialValue}>£{formatNumber(financials.deposit)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Brokerage (1%)</span>
                  <span style={styles.financialValue}>£{formatNumber(financials.brokage)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Stamp Duty</span>
                  <span style={styles.financialValue}>£{formatNumber(financials.stampDuty)}</span>
                </div>
                <div style={{ 
                  ...styles.financialItem, 
                  backgroundColor: theme.color1,
                  color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff'
                }}>
                  <span style={{ 
                    ...styles.financialLabel, 
                    fontWeight: '700',
                    color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff'
                  }}>Total Investment</span>
                  <span style={{ 
                    ...styles.financialValue, 
                    fontSize: '16px',
                    color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff'
                  }}>£{formatNumber(financials.totalInvestment)}</span>
                </div>
                <div style={styles.divider}></div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Market Rent</span>
                  <span style={styles.financialValue}>£{formatNumber(financials.averageRent)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Monthly Mortgage</span>
                  <span style={styles.financialValue}>£{formatNumber(financials.mortgage)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span style={styles.financialLabel}>Net Monthly Cashflow</span>
                  <span style={{ ...styles.financialValue, color: '#000000' }}>
                    £{formatNumber(financials.netCashflow)}
                  </span>
                </div>
                <div style={{ 
                  ...styles.financialItem, 
                  backgroundColor: theme.color2,
                  color: isWhiteOrShade(theme.color2) ? theme.txtColor : '#ffffff'
                }}>
                  <span style={{ 
                    ...styles.financialLabel, 
                    fontWeight: '700',
                    color: isWhiteOrShade(theme.color2) ? theme.txtColor : '#ffffff'
                  }}>Net Annual Cashflow</span>
                  <span style={{ 
                    ...styles.financialValue, 
                    fontSize: '16px', 
                    color: isWhiteOrShade(theme.color2) ? theme.txtColor : '#ffffff'
                  }}>
                    £{formatNumber(financials.netAnnualCashflow)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Property Description Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>Property Details</h3>
                <p style={styles.cardSubtitle}>Comprehensive property information</p>
              </div>
              <div style={styles.descriptionText}>
                {property.results.propertyDescription || 'No property description available for this listing. Please contact us for more detailed information about this investment opportunity.'}
              </div>
            </div>
          </div>
        </div>

        {/* Page break for Market Comparables */}
        <div style={{ pageBreakBefore: 'always' }}>
          {/* Header repeat for page 4 */}
          <div style={styles.header}>
            <div style={styles.headerContent}>
              <div style={styles.logoSection}>
                <img src={theme.logo} alt="Logo" style={styles.logo} />
                <div style={styles.reportTitle}>Property Investment Report</div>
              </div>
              {!hideQRCode && (
                <div style={styles.qrSection}>
                  <div style={{ ...styles.qrCode, ...qrCodeStyle }}>
                    <QRCodeCanvas 
                      value={propertyUrl} 
                      size={80} 
                      fgColor={qrCodeFgColor}
                      bgColor={qrCodeBgColor}
                    />
                  </div>
                  <p style={styles.qrText}>Scan to view online</p>
                </div>
              )}
            </div>
          </div>

          {/* Comparable Data */}
          <div style={styles.card} className="mt-6">
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Market Comparables</h3>
              <p style={styles.cardSubtitle}>Recent sales and rental data in the area</p>
            </div>
          <div style={styles.comparablesGrid}>
            {comparableTypes.map((type, index) => (
              <div key={index} style={styles.comparableCard}>
                <h4 style={styles.comparableTitle}>{type.title}</h4>
                {type.data && Array.isArray(type.data) && type.data.length > 0 ? (
                  type.data.slice(0, 3).map((comp: string, compIndex: number) => {
                    const [address, price] = comp.split('-').map((item) => item.trim());
                    return (
                      <div key={compIndex} style={styles.comparableItem}>
                        <div style={styles.comparableAddress}>{address}</div>
                        <div style={styles.comparablePrice}>{price}</div>
                      </div>
                    );
                  })
                ) : (
                  <div style={styles.comparableItem}>
                    <div style={styles.comparableAddress}>Not Available</div>
                    <div style={styles.comparablePrice}>-</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        </div>

        {/* Page break for Disclaimer */}
        <div style={{ pageBreakBefore: 'always' }}>
          {/* Header repeat for page 5 */}
          <div style={styles.header}>
            <div style={styles.headerContent}>
              <div style={styles.logoSection}>
                <img src={theme.logo} alt="Logo" style={styles.logo} />
                <div style={styles.reportTitle}>Property Investment Report</div>
              </div>
              {!hideQRCode && (
                <div style={styles.qrSection}>
                  <div style={{ ...styles.qrCode, ...qrCodeStyle }}>
                    <QRCodeCanvas 
                      value={propertyUrl} 
                      size={80} 
                      fgColor={qrCodeFgColor}
                      bgColor={qrCodeBgColor}
                    />
                  </div>
                  <p style={styles.qrText}>Scan to view online</p>
                </div>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div style={styles.disclaimer}>
          <div style={styles.disclaimerTitle}>Important Disclaimer</div>
          <p style={{ marginBottom: '12px' }}>
            This report is for informational purposes only and should not be viewed as legal, tax, or investment advice. 
            The information provided does not constitute a solicitation, recommendation, or endorsement in this or any other jurisdiction.
          </p>
          <p style={{ marginBottom: '12px' }}>
            While we strive to ensure accuracy, property information can change rapidly. Gross yield calculations use standard 
            industry formulas and rental estimates are based on comparable properties in the area. EPC information is sourced 
            from UK government databases.
          </p>
          <p>
            We strongly recommend seeking independent professional advice before making any investment decisions. Stock listed 
            may occasionally be sold or withdrawn at short notice.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

// PropertyPDFDownloadLink component (keeping the existing functionality)
const PropertyPDFDownloadLink = ({ property, theme }: { property: any, theme: any }) => {
  const [status, setStatus] = useState<'idle' | 'generating' | 'ready'>('idle');
  const [showModal, setShowModal] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const generatePDF = useCallback(() => {
    setStatus('generating');
    
    setTimeout(() => {
      const element = pdfRef.current;

      if (!element) {
        console.error('PDF element not found');
        setStatus('ready');
        return;
      }

      const opt = {
        margin: [4, 4, 4, 4],
        filename: `${property?.results?.propertyOf || 'property'}-investment-report.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          letterRendering: true,
        },
        jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' },
      };

      const images = element.getElementsByTagName('img');
      const imagePromises = Array.from(images).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => {
                console.error(`Error loading image: ${img.src}`);
                resolve();
              };
            }
          })
      );

      Promise.all(imagePromises)
        .then(() => {
          html3pdf()
            .from(element)
            .set(opt)
            .save()
            .then(() => {
              setStatus('ready');
              setShowModal(false);
            })
            .catch((error: any) => {
              console.error('Error during PDF generation:', error);
              setStatus('ready');
            });
        })
        .catch((error) => {
          console.error('Error loading images:', error);
          setStatus('ready');
        });
    }, 100);
  }, [property]);

  const handleClick = () => {
    setShowModal(true);
  };

  const modalStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      padding: isMobile ? '10px' : '0',
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '24px',
      width: isMobile ? '100%' : '90%',
      maxWidth: isMobile ? '100%' : '1000px',
      minWidth: isMobile ? '100%' : '800px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      zIndex: 10000,
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '24px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '12px',
    },
    button: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      fontSize: '14px',
    },
    generateButton: {
      backgroundColor: theme.color1 || '#3b82f6',
      color: isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff',
    },
    closeButton: {
      backgroundColor: '#f3f4f6',
      color: '#000000',
    },
  };

  const MobilePreviewContent = () => (
    <div style={{ fontFamily: 'Inter, sans-serif', padding: '16px' }}>
      <div style={{
        background: `linear-gradient(135deg, ${theme.color1} 0%, ${lighten(0.1, theme.color1)} 100%)`,
        padding: '20px',
        borderRadius: '12px',
        color: isWhiteOrShade(theme.color1) ? '#1f2937' : '#ffffff',
        marginBottom: '16px',
      }}>
        <img src={theme.logo} alt="Logo" style={{ maxWidth: '120px', maxHeight: '40px', marginBottom: '12px' }} />
        <h2 style={{ fontSize: '18px', fontWeight: '700', margin: '0' }}>Investment Report Preview</h2>
      </div>
      <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0' }}>{property.results.propertyOf}</h3>
        <p style={{ fontSize: '14px', color: '#059669', fontWeight: '700', margin: '0' }}>
          £{property.results.price ? property.results.price.toLocaleString() : 'N/A'}
        </p>
      </div>
      <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
        Generate PDF to view the complete investment analysis with financial projections, property details, and market comparables.
      </p>
    </div>
  );

  return (
    <>
      <button
        onClick={handleClick}
        className="text-blue-500 hover:text-blue-700 focus:outline-none transition-all duration-200 hover:scale-110"
        aria-label="Download Investment Report"
      >
        <FontAwesomeIcon icon={faFilePdf} size="lg" />
      </button>

      {showModal &&
        ReactDOM.createPortal(
          <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
              {isMobile && <MobilePreviewContent />}
              <div 
                style={{ 
                  position: 'relative', 
                  height: isMobile ? 0 : '60vh', 
                  overflow: isMobile ? 'hidden' : 'auto',
                  visibility: isMobile ? 'hidden' : 'visible',
                  border: isMobile ? 'none' : '1px solid #e2e8f0',
                  borderRadius: '12px',
                }}
              >
                <div ref={pdfRef}>
                  <PropertyPDFGenerator property={property} theme={theme} />
                </div>
              </div>
              {status === 'generating' && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10001,
                  borderRadius: '16px',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '4px solid #e2e8f0',
                      borderTop: `4px solid ${theme.color1}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 16px auto',
                    }}></div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>Generating Report</h3>
                    <p style={{ color: '#64748b', margin: '0' }}>Please wait while we create your investment analysis...</p>
                  </div>
                </div>
              )}
              <div style={modalStyles.buttonContainer}>
                <button
                  onClick={generatePDF}
                  style={{ ...modalStyles.button, ...modalStyles.generateButton }}
                  disabled={status === 'generating'}
                  onMouseEnter={(e) => {
                    if (status !== 'generating') {
                      e.currentTarget.style.backgroundColor = theme.color2;
                      e.currentTarget.style.color = isWhiteOrShade(theme.color2) ? theme.txtColor : '#ffffff';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = theme.color1;
                    e.currentTarget.style.color = isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {status === 'generating' ? 'Generating Report...' : 'Download Investment Report'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ ...modalStyles.button, ...modalStyles.closeButton }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.color1;
                    e.currentTarget.style.color = isWhiteOrShade(theme.color1) ? theme.txtColor : '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.color = '#000000';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default PropertyPDFDownloadLink;